import { assert } from 'assert-ts';
import { Fragment } from 'react';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import {
  CONFIG_OVERRIDE_LABELS,
  type ConfigKey,
} from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { SelectGroup } from '#components/patterns/forms/select/select-group/SelectGroup.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectSeparator } from '#components/patterns/forms/select/select-separator/SelectSeparator.tsx';
import { Select } from '#components/patterns/forms/select/select/Select.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { isDNSConfigOverrideKey } from '#util/unknownConfigOverrides.ts';

export const CONFIG_OVERRIDE_GROUPS = [
  [
    'IP preferences',
    [
      'lighthouse.remote_allow_list',
      'lighthouse.local_allow_list',
      'lighthouse.advertise_addrs',
      'lighthouse.interval',
      'preferred_ranges',
      'listen.host',
    ],
  ],
  ['DNS', ['definednet.dns.resolver_addrs', 'definednet.dns.match_domains', 'definednet.dns.search_domains']],
  ['TUN', ['tun.disabled', 'tun.dev', 'tun.mtu', 'tun.tx_queue']],
  [
    'Performance tuning',
    [
      'listen.batch',
      'listen.read_buffer',
      'listen.write_buffer',
      'routines',
      'listen.udp_offloads',
      'tun.use_offloads',
    ],
  ],
  ['Logging', ['logging.level', 'logging.format', 'logging.timestamp_format']],
  ['Static map', ['static_map.network']],
  ['Punchy', ['punchy.delay', 'punchy.respond_delay']],
  // Stats doesn't really fit the above pattern, we don't list out the overrides, but rather the types
  ['Stats', ['prometheus', 'graphite']],
] as const;

type Props = {
  onChange: (type: ConfigKey) => void;
  value: ConfigKey | '';
  errorMessage?: string;
  existingOverrides: ConfigOverride[];
};

export function ConfigChooser({ onChange, value, errorMessage, existingOverrides }: Props) {
  const { data: { DNS_CONFIG_OVERRIDES } = {} } = useSuspenseFeatureFlagsQuery();

  // filter out already-configured keys to avoid duplicates, but remote and local allow list rules can always be added.
  const filteredConfigOverrideGroups = CONFIG_OVERRIDE_GROUPS.map(([label, overrideKeys]) => {
    const filteredOverrideKeys = (overrideKeys as unknown as ConfigKey[]).filter((key) => {
      if (!DNS_CONFIG_OVERRIDES && isDNSConfigOverrideKey(key)) return false;

      if (key === 'graphite' || key === 'prometheus') {
        return !existingOverrides.find((existingOverride) => existingOverride.key === 'stats.type');
      }

      if (key === 'lighthouse.local_allow_list' || key === 'lighthouse.remote_allow_list') {
        // It is always possible to add more allow list items
        return true;
      }

      return !existingOverrides.find((existingOverride) => existingOverride.key === key);
    });

    return [label, filteredOverrideKeys];
  }).filter((group): group is [(typeof CONFIG_OVERRIDE_GROUPS)[number][0], ConfigKey[]] => Boolean(group[1]?.length));

  return (
    <Select
      className="width-100"
      label="Select option"
      placeholder="Select…"
      errorMessage={errorMessage}
      onChange={(v) => {
        // Just for TypeScript: the empty string is only the initial "nothing chosen yet" state,
        // it can't actually be selected
        if (v) {
          onChange(v);
        }
      }}
      value={value}
      renderValue={(v: ConfigKey | '') => (v ? CONFIG_OVERRIDE_LABELS[v] : null)}
    >
      {filteredConfigOverrideGroups.map((group, index) => {
        const label = assert(group[0]);
        const overrideKeys = assert(group[1]);
        return (
          <Fragment key={label}>
            <SelectGroup label={label}>
              {overrideKeys.map((v) => (
                <SelectItem key={v} value={v}>
                  {CONFIG_OVERRIDE_LABELS[v]}
                </SelectItem>
              ))}
            </SelectGroup>
            {index === filteredConfigOverrideGroups.length - 1 ? null : <SelectSeparator />}
          </Fragment>
        );
      })}
    </Select>
  );
}
